import { useState } from 'react';
import { PersonnelConfig, Service, Competence } from '../../../../shared/types/config';
import configService from '../../../../shared/services/configService';

interface UsePersonnelConfigParams {
  personnelFilterService: number | 'all';
  setError: (msg: string | null) => void;
  setSuccess: (msg: string | null) => void;
  loadAllData: () => Promise<void>;
}

export function usePersonnelConfig({
  personnelFilterService,
  setError,
  setSuccess,
  loadAllData,
}: UsePersonnelConfigParams) {
  const [personnelConfigs, setPersonnelConfigs] = useState<PersonnelConfig[]>([]);
  const [searchPersonnel, setSearchPersonnel] = useState('');
  const [showPersonnelModal, setShowPersonnelModal] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState<PersonnelConfig | null>(null);
  const [personnelForm, setPersonnelForm] = useState<Omit<PersonnelConfig, 'id'>>({
    grade: '',
    prix: 0,
    id_competences: [],
    id_service: 0,
    ordre: 0,
  });

  const handleAddPersonnel = () => {
    setEditingPersonnel(null);
    const preService = personnelFilterService === 'all' ? 0 : personnelFilterService;
    setPersonnelForm({
      grade: '',
      prix: 0,
      id_competences: [],
      id_service: preService,
      ordre: 0,
    });
    setShowPersonnelModal(true);
  };

  const handleEditPersonnel = (personnel: PersonnelConfig) => {
    setEditingPersonnel(personnel);
    setPersonnelForm({
      grade: personnel.grade,
      prix: personnel.prix,
      id_competences: personnel.id_competences || [],
      id_service: personnel.id_service,
      ordre: personnel.ordre ?? 0,
    });
    setShowPersonnelModal(true);
  };

  const handleSavePersonnel = async () => {
    try {
      setError(null);
      let result;
      if (editingPersonnel?.id) {
        result = await configService.updatePersonnelConfig(editingPersonnel.id, personnelForm);
      } else {
        result = await configService.createPersonnelConfig(personnelForm);
      }
      if (result.success) {
        setSuccess(editingPersonnel ? 'Personnel modifié avec succès' : 'Personnel ajouté avec succès');
        setShowPersonnelModal(false);
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeletePersonnel = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce personnel ?')) return;
    try {
      setError(null);
      const result = await configService.deletePersonnelConfig(id);
      if (result.success) {
        setSuccess('Personnel supprimé avec succès');
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  return {
    personnelConfigs,
    setPersonnelConfigs,
    searchPersonnel,
    setSearchPersonnel,
    showPersonnelModal,
    setShowPersonnelModal,
    editingPersonnel,
    personnelForm,
    setPersonnelForm,
    handleAddPersonnel,
    handleEditPersonnel,
    handleSavePersonnel,
    handleDeletePersonnel,
  };
}
